import { v4 } from 'uuid';
import { z } from 'zod';

/**
 * Base schema for all model entities in the system.
 * Provides common fields for tracking entity lifecycle and metadata.
 *
 * @property {number} id - Unique numeric identifier (min 0)
 * @property {string} guid - Unique UUID identifier (auto-generated)
 * @property {number} [createdBy] - ID of the user who created the entity
 * @property {string} [createdByName] - Name of the user who created the entity
 * @property {string} [createdOn] - Timestamp when the entity was created
 * @property {number} [updatedBy] - ID of the user who last updated the entity
 * @property {string} [updatedByName] - Name of the user who last updated the entity
 * @property {string} [updatedOn] - Timestamp when the entity was last updated
 * @property {boolean} isActive - Whether the entity is active in the system (defaults to true)
 */
export const baseModelSchema = z.object({
    id: z.number().min(0, 'ID is required').default(0),
    guid: z.string().uuid().default(() => v4()),
    createdBy: z.number().optional(),
    createdByName: z.string().optional(),
    createdOn: z.string().optional(),
    updatedBy: z.number().optional(),
    updatedByName: z.string().optional(),
    updatedOn: z.string().optional(),
    isActive: z.boolean().default(true),
});

/**
 * Base schema for application view models.
 * Similar to baseModelSchema but uses GUIDs for user references instead of IDs.
 *
 * @property {number} id - Unique numeric identifier (min 0)
 * @property {string} guid - Unique UUID identifier (auto-generated)
 * @property {string} [createdByGuid] - GUID of the user who created the entity
 * @property {string} [createdByName] - Name of the user who created the entity
 * @property {string} [createdOn] - Timestamp when the entity was created
 * @property {string} [updatedByGuid] - GUID of the user who last updated the entity
 * @property {string} [updatedByName] - Name of the user who last updated the entity
 * @property {string} [updatedOn] - Timestamp when the entity was last updated
 * @property {boolean} isActive - Whether the entity is active in the system (defaults to true)
 */
export const baseModelAppViewSchema = z.object({
    id: z.number().min(0, 'ID is required').default(0),
    guid: z.string().uuid().default(() => v4()),
    createdByGuid: z.string().optional(),
    createdByName: z.string().optional(),
    createdOn: z.string().optional(),
    updatedByGuid: z.string().optional(),
    updatedByName: z.string().optional(),
    updatedOn: z.string().optional(),
    isActive: z.boolean().default(true),
});

/**
 * TypeScript interface for the base model.
 * Used as a foundation for all entity models in the system.
 */
export type IBaseModel = z.infer<typeof baseModelSchema>;

/**
 * TypeScript interface for the application view model.
 * Used as a foundation for all view models in the application.
 */
export type IBaseModelAppView = z.infer<typeof baseModelAppViewSchema>;
